<template>
  <div class="flex gap-2 md:gap-4">
    <Modal
      open
      :title="__('Profile Overview')"
      @close="handleCancelButtonClick"
    >
      <form @submit.prevent="handleSubmit">
        <div class="grid gap-6 md:grid-cols-3">
          <InputTextarea
            id="tagline"
            v-model:value="values.tagline"
            v-model:errors="errors.tagline"
            :label="__('Add a short bio to introduce yourself (optional)')"
            :placeholder="__('Max 100 characters')"
            class="md:col-span-3"
          />

          <InputText
            id="linkedin"
            v-model:value="values.links.linkedin"
            v-model:errors="errors.linkedin"
            :label="__('LinkedIn')"
            :placeholder="__('Add LinkedIn')"
          />
          <InputText
            id="behance"
            v-model:value="values.links.behance"
            v-model:errors="errors.behance"
            :label="__('Add Behance')"
            :placeholder="__('Add Behance')"
          />
          <InputText
            id="github"
            v-model:value="values.links.github"
            v-model:errors="errors.github"
            :label="__('Add GitHub')"
            :placeholder="__('Add GitHub')"
          />

          <InputText
            id="other"
            v-model:value="values.links.other"
            v-model:errors="errors.other"
            :label="__('Add another professional profile')"
            :placeholder="__('Add another professional profile')"
            class="md:col-span-3"
          />
        </div>

        <div
          class="mt-8 flex flex-col gap-2 md:flex-row md:justify-center"
        >
          <Button
            class="flex items-center justify-center gap-1.5"
            :type="isLoading ? 'button' : 'submit'"
            :disabled="isLoading"
          >
            <Icon
              v-if="isLoading"
              class="animate-spin text-lg"
              name="progress_activity"
            />
            <span>{{ isLoading ? __('Loading...') : __('Save') }}</span>
          </Button>

          <Button variant="ghost" @click="handleCancelButtonClick">
            <span>{{ __('Cancel') }}</span>
          </Button>
        </div>
      </form>
    </Modal>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { useToast } from 'vue-toastification';
  import { __ } from '@/helpers';
  import { Button } from '@/components/block/button';
  import { Icon } from '@/components/block/icon';
  import { Modal } from '@/components/block/modal';
  import { InputText } from '@/components/form/input-text';
  import { InputTextarea } from '@/components/form/input-textarea';

  const props = defineProps({
    data: {
      type: [Object, null],
      required: true
    }
  });

  const emits = defineEmits(['cancel', 'save']);

  const toast = useToast();

  const userData = ref(JSON.parse(JSON.stringify(props.data)));

  const values = ref({
    tagline: userData.value?.tagline || '',
    links: {
      linkedin: userData.value?.links?.linkedin || '',
      github: userData.value?.links?.github || '',
      behance: userData.value?.links?.behance || '',
      other: userData.value?.links?.other || ''
    }
  });

  const errors = ref({
    tagline: [],
    linkedin: [],
    behance: [],
    github: [],
    other: []
  });

  const isLoading = ref(false);

  function handleCancelButtonClick() {
    emits('cancel');
  }

  async function handleSubmit() {
    let firstElement;

    if (Object.values(errors.value).some((error) => error.length)) {
      if (firstElement) {
        firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    isLoading.value = true;

    try {
      let response;
      response = await axios.post(
        route('redesign.profile.updateProfile'),
        {
          ...values.value
        }
      );

      if (response?.data?.message) {
        toast.success(response.data.message);
      }

      emits('save', {
        data: values.value
      });
    } catch (error) {
      if (error?.response?.data?.errors) {
        errors.value = error.response.data.errors;
      } else {
        toast.error(
          __('An unexpected error has occured. Please try again later.')
        );
      }
    }

    isLoading.value = false;
  }
</script>
